import { Direction } from '@angular/cdk/bidi';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ElementRef, Injectable, QueryList } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Store } from '@ngrx/store';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { Artist } from '@shared/models/artist';
import { Interest } from '@shared/models/interest';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import { FollowCard, Preference } from '@shared/models/preferences';
import { ProductType } from '@shared/models/product-type';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { ISiteSettingsSwitches } from '@shared/shared/interfaces/site-settings.interfaces';
import { FollowButtonStyleEnum, NotificationTexts, PreferenceTracking } from '@shared/shared/modules/card/models/card';
import { NotificationService } from '@shared/shared/modules/notification/services/notification.service';
import { getMemberProfileFeatureState } from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, filter, first, map, mapTo, switchMap, tap } from 'rxjs/operators';
import { ConfirmationPopupComponent } from '../../pages/preferences/components/confirmation-popup/confirmation-popup.component';
import { TogglePreferencePayload } from '../../pages/preferences/models/toggle-preference-payload';
import { ServerFavourite } from '../models/server-favourite';

@Injectable({ providedIn: 'root' })
export class PreferencesService {
  private followedArtistCount: number;
  private profileId$: Observable<number>;

  public readonly followArtistsEnabled$: Observable<boolean>;
  public readonly favouritesEnabled$: Observable<boolean>;
  public readonly followInterestsEnabled$: Observable<boolean>;
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private store: Store,
    private siteSettings: SiteSettingsService,
    private angulartics2Segment: Angulartics2Segment,

    private notificationService: NotificationService
  ) {
    this.profileId$ = this.store.select(getMemberProfileFeatureState).pipe(
      filter((state) => state.loaded),
      map((profile) => profile.id)
    );
    const switches$ = this.siteSettings.fetchSiteSettings().pipe(map((settings) => settings.switches));

    this.followArtistsEnabled$ = switches$.pipe(map((switches) => switches.preferences_artist));
    this.favouritesEnabled$ = switches$.pipe(
      map(
        (switches) =>
          switches.preferences_artist ||
          switches.preferences_dining ||
          switches.preferences_travel ||
          switches.preferences_member_benefits
      )
    );
    this.followInterestsEnabled$ = switches$
      .pipe(filter((switches: ISiteSettingsSwitches) => !!switches))
      .pipe(
        map(
          (switches: ISiteSettingsSwitches) =>
            switches.preferences_interests && switches.preferences_follow_events_and_entertainment
        )
      );
  }

  public searchArtists(
    initialFilter = '',
    searchPhrase = '',
    followingFilter = false,
    lastId: number,
    page_size = 25
  ): Observable<PaginatedApiResponse<Artist>> {
    let params = new HttpParams();
    params = params.append('page_size', page_size.toString());

    if (lastId) {
      params = params.append('last_id', lastId.toString());
    }

    if (searchPhrase) {
      params = params.append('search', searchPhrase);
    }

    if (initialFilter) {
      params =
        initialFilter.length === 1
          ? params.append('startswith', initialFilter)
          : params.append('startswith_digits', '');
    }

    if (followingFilter) {
      params = params.append('is_followed', true);
    }

    if (!searchPhrase && !initialFilter && !followingFilter) {
      params = params.append('site_country_only', '');
    }

    return this.http.get<PaginatedApiResponse<Artist>>(ApiEndpoint.ENTERTAINMENT_ARTISTS, { params });
  }

  public getRandomPreferences(): Observable<FollowCard[]> {
    return this.http
      .get<Preference[]>(ApiEndpoint.MEMBER_PREFERENCES_POPUP)
      .pipe(
        map((response: Preference[]) => response.map((preferences: Preference) => this.mapPreferencesCard(preferences)))
      );
  }

  public getFavourites(
    types: ProductType[],
    lastId?: number,
    lastType?: ProductType
  ): Observable<PaginatedApiResponse<ServerFavourite>> {
    return this.profileId$.pipe(
      first(),
      switchMap((profileId) => {
        const params: { [key: string]: any } = {
          page_size: types.includes(ProductType.Brand) || types.includes(ProductType.EntertainmentEvent) ? '12' : '25',
          type: types,
          member_profile: `${profileId}`,
        };

        if (lastId) {
          params.last_id = `${lastId}`;
        }

        if (lastType && types.includes(lastType)) {
          params.last_type = `${lastType}`;
        }

        return this.http.get<PaginatedApiResponse<ServerFavourite>>(ApiEndpoint.MEMBER_PREFERENCES, { params });
      })
    );
  }

  public togglePreference(
    id: number,
    preferenceTracking: PreferenceTracking,
    type: ProductType,
    active: boolean,
    name: string,
    notificationTexts?: NotificationTexts
  ): Observable<number> {
    const payload: TogglePreferencePayload = {
      entity_id: id,
      type,
      name,
    };
    return combineLatest([
      this.initCount(payload.type), // follow primary interest and follow artist count
      this.profileId$.pipe(
        first(),
        switchMap((profileId) => {
          const params: { [key: string]: string } = { member_profile: `${profileId}` };
          return this.http.post(ApiEndpoint.MEMBER_PREFERENCES, payload, { params });
        })
      ),
    ]).pipe(
      tap(() => {
        const data = {
          page_url: window.location.href,
          type: active ? 'remove' : 'add',
          ...preferenceTracking.data,
        };

        this.angulartics2Segment.eventTrack(preferenceTracking.eventName, data);

        if (type === ProductType.Artist) {
          if (active) {
            this.followedArtistCount--;
          } else {
            this.followedArtistCount++;

            if (this.followedArtistCount === 1) {
              this.dialog.open(ConfirmationPopupComponent, {
                width: '600px',
                ariaLabel: 'follow artist confirmation dialog',
                data: { artist: preferenceTracking?.data.name },
                direction: document.querySelector('html')?.dir as Direction,
              });
            }
          }
        }

        if (notificationTexts) {
          this.notificationService.showNotification(
            active ? notificationTexts.labels.off : notificationTexts.labels.on,
            notificationTexts.labels.params,
            active ? notificationTexts.ariaLabels.off : notificationTexts.ariaLabels.on
          );
        }
      }),
      mapTo(id),
      catchError((err) => this.showError(err))
    );
  }

  private initCount(type: ProductType): Observable<number> {
    if (type === ProductType.Artist || type === ProductType.PrimaryInterest) {
      return this.getFavourites([type]).pipe(
        map((response) => response.count),
        tap((count) => (this.followedArtistCount = count))
      );
    } else {
      return of(0);
    }
  }

  private showError<T>(err: T): Observable<T> {
    this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 5000 });
    return throwError(err);
  }

  public searchPrimaryInterest(
    page_size: number,
    page: number,
    searchPhrase = '',
    selectedFilter = []
  ): Observable<PaginatedApiResponse<Interest>> {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('page_size', page_size.toString());

    if (searchPhrase) {
      params = params.append('search', searchPhrase);
    }

    if (selectedFilter) {
      selectedFilter.forEach((item) => {
        params = params.append(item.name, item.value);
      });
    }

    if (!searchPhrase && !selectedFilter) {
      params = params.append('site_country_only', '');
    }

    return this.http.get<PaginatedApiResponse<Interest>>(ApiEndpoint.PRIMARY_INTERESTS, { params });
  }

  public getRandomArtists(pageSize: number, page = 1): Observable<FollowCard[]> {
    let params = new HttpParams();
    params = params.append('page_size', pageSize.toString());
    params = params.append('page', page.toString());

    return this.http
      .get<PaginatedApiResponse<Artist>>(ApiEndpoint.ENTERTAINMENT_RANDOM_ARTISTS, { params })
      .pipe(map((paginatedResponse) => paginatedResponse.results.map((artist: Artist) => this.mapArtistCard(artist))));
  }

  public focusToNextCard(pageNumber: number, cardsCount: number, result: QueryList<ElementRef>): void {
    setTimeout(() => {
      const nextCardIndex = (pageNumber - 1) * cardsCount;
      const nextCard = result.toArray()[nextCardIndex];
      if (nextCard) {
        nextCard.nativeElement.children[0].focus();
      }
    });
  }

  private mapPreferencesCard(preferences: Preference): FollowCard {
    return {
      name: preferences.name,
      imageUrl: preferences.image,
      followButton: {
        style: FollowButtonStyleEnum.Shadowed,
        preferred: preferences.is_preferred,
        preferenceTracking: {
          eventName: 'Pop-up - Follow / Unfollow button',
          data: {
            id: preferences.id,
            name: preferences.name,
            primaryInterest: preferences.name,
            category: preferences.interest_type,
          },
        },
        texts: {
          on: 'Preferences:Following',
          off: preferences.entity_type === ProductType.Artist ? 'Preferences:FollowArtist' : 'Preferences:Follow',
        },
        product: {
          type: preferences.entity_type === ProductType.Artist ? ProductType.Artist : ProductType.PrimaryInterest,
          id: preferences.id,
          name: preferences.name,
          category: preferences.interest_type,
        },
        notificationTexts: {
          labels: {
            on:
              preferences.entity_type === ProductType.Artist
                ? 'Preferences:ConfirmFollowArtist'
                : 'Preferences:ConfirmFollow',
            off:
              preferences.entity_type === ProductType.Artist
                ? 'Preferences:ConfirmUnfollowArtist'
                : 'Preferences:ConfirmUnfollow',
            params:
              preferences.entity_type === ProductType.Artist
                ? {
                    artist_name: preferences.name,
                  }
                : {
                    interest_name: preferences.name,
                  },
          },
          ariaLabels: {
            on:
              preferences.entity_type === ProductType.Artist
                ? 'Preferences:ReaderArtistFollowed'
                : 'Preferences:ReaderEventAdded',
            off:
              preferences.entity_type === ProductType.Artist
                ? 'Preferences:ReaderArtistUnfollowed'
                : 'Preferences:ReaderEventRemoved',
          },
        },
      },
    };
  }

  private mapArtistCard(artist: Artist): FollowCard {
    return {
      name: artist.name,
      imageUrl: artist.image,
      followButton: {
        style: FollowButtonStyleEnum.Shadowed,
        preferred: artist.is_preferred,
        preferenceTracking: {
          eventName: 'Preferences_Favourite artist selected homepage popup',
          data: {
            id: artist.id,
            name: artist.name,
          },
        },
        texts: {
          on: 'Preferences:Following',
          off: 'Preferences:FollowArtist',
        },
        product: {
          type: ProductType.Artist,
          id: artist.id,
          name: artist.name,
        },
        notificationTexts: {
          labels: {
            on: 'Preferences:ConfirmFollowArtist',
            off: 'Preferences:ConfirmUnfollowArtist',
            params: {
              artist_name: artist.name,
            },
          },
          ariaLabels: {
            on: 'Preferences:ReaderArtistFollowed',
            off: 'Preferences:ReaderArtistUnfollowed',
          },
        },
      },
    };
  }
}
